/**
 * @fileoverview Builds message debug payload and opens the KorvoChat debug modal.
 * @purpose Extracted from KorvoChat.handleShowDebugInfo for lower CRAP / clearer testing surface.
 */

import type ThreadsClient from '@korvo/portal-sdk/threads-client';

import {
  buildKorvoChatMessageDebugInfoData,
  type KorvoChatThreadConfigSnapshot,
} from './korvo-chat-message-debug-info';
import type { ChatMessage, ChatSession, KorvoChatConfig } from './types';

export async function runKorvoChatMessageDebugFlow(
  messageId: string,
  messages: ChatMessage[],
  currentSession: ChatSession | null,
  threadsClient: Pick<ThreadsClient, 'getThread'>,
  config: KorvoChatConfig,
  onApplyDebugInfo: (data: Record<string, unknown>) => void,
  onOpenModal: () => void,
): Promise<void> {
  const message = messages.find(m => m.id === messageId);
  if (!message) {
    return;
  }

  let mentorAgentDebug: Record<string, unknown> | null = null;
  let threadConfig: KorvoChatThreadConfigSnapshot = null;

  if (message.role === 'assistant' && currentSession?.threadId) {
    try {
      const threadData = (await threadsClient.getThread(currentSession.threadId)) as {
        config?: { model?: string; provider?: string; temperature?: number };
      };
      threadConfig = threadData.config || null;
    } catch {
      threadConfig = null;
    }
  }

  if (message.role === 'assistant' && currentSession?.threadId) {
    try {
      const moduleName = config.portal.packageName;
      const res = await fetch(
        `/api/${moduleName}/threads/${encodeURIComponent(String(currentSession.threadId))}/debug`,
      );
      if (res.ok) {
        mentorAgentDebug = (await res.json()) as Record<string, unknown>;
      }
    } catch {
      mentorAgentDebug = null;
    }
  }

  const msgExt = message as ChatMessage & {
    createdAt?: string;
    updatedAt?: string;
    processingTime?: number;
    tokensIn?: number;
    tokensOut?: number;
    costCents?: number;
    isQueryMessage?: boolean;
    isHelperMessage?: boolean;
    ragResults?: unknown[];
  };

  const debugInfoData = buildKorvoChatMessageDebugInfoData({
    message: msgExt,
    currentSession,
    config,
    threadConfig,
    mentorAgentDebug,
  });

  onApplyDebugInfo(debugInfoData);
  onOpenModal();
}
